import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';

import {
  AlertBox,
  AMLCheckTabs,
  Button,
  ChainSelector,
  ConfirmActionModal,
  Input,
  InputSwitch,
  SectionBox,
  SvgIcon,
} from '@/components';

import {
  useApiCall,
  useDebounce,
  useInputBool,
  useInputText,
  useModal,
} from '@/hooks';
import { Validation } from '@/models';
import {
  ChainOptions,
  ChainsEnum,
  ErrorCodeEnum,
  DEBOUNCE_DELAY,
  ChainsDictionary,
  addressCheckPagePath,
} from '@/config';

import { useAppDispatch, useAppSelector } from '@/redux/hooks';

import {
  amlbotAPI,
  AmlCheckRequest,
  GetTxReceiversRequest,
  GetTxReceiversResponse,
  SideEnum,
  WhichHash,
  WhichHashRequest,
} from '@/api';
import { amlCheckActions } from '@/redux/actions';
import {
  amlCheckSelectors,
  profileSelectors,
  serviceOptionsSelectors,
  userSelectors,
} from '@/redux/selectors';
import { showEmailConfirmationToast } from '@/helpers';

import { BatchUpload, TransactionReceivers } from './components';

import styles from './AmlChecker.module.sass';

interface AmlCheckerProps {}

const AmlChecker: FC<AmlCheckerProps> = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const amlRef = useRef<HTMLDivElement>(null);

  const {
    loading,
    error: amlError,
    errorCode: amlErrorCode,
  } = useAppSelector(amlCheckSelectors.getAmlCheckSlice);
  const { data: serviceOptions } = useAppSelector(
    serviceOptionsSelectors.getServiceOptionsSelectors,
  );
  const { data: userData } = useAppSelector(userSelectors.getUserSlice);
  const devModeUsed = useAppSelector(userSelectors.checkIfDevModeUsed);
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const isAccountLiteMode = useAppSelector(profileSelectors.isAccountLiteMode);

  const [whichLoading, setWhichLoading] = useState(false);
  const [receiverInvalid, setReceiverInvalid] = useState(false);
  const [options, setOptions] = useState(ChainOptions);
  const [suggestions, setSuggestions] = useState<WhichHash>({
    addresses: [],
    txs: [],
  });
  const monitoringBonusCount = useAppSelector(
    profileSelectors.monitoringBonusCount,
  );
  const monitoringAllowed = useMemo(
    () => (monitoringBonusCount ?? 0) > 0,
    [monitoringBonusCount],
  );

  const isComplianceActivated = useAppSelector(
    userSelectors.complianceActivated,
  );

  const modeSupportedChains = useMemo(() => {
    if (userData?.amlFlow && serviceOptions?.modeAssets[userData?.amlFlow]) {
      return serviceOptions?.modeAssets[userData?.amlFlow];
    }
  }, [userData?.amlFlow, serviceOptions?.modeAssets]);

  const directionOptions = useMemo(
    () => [
      {
        value: SideEnum.DEPOSIT,
        label: t('translation.check_deposit_side_string'),
      },
      {
        value: SideEnum.WITHDRAWAL,
        label: t('translation.check_wd_side_string'),
      },
    ],
    [userData?.locale],
  );
  const [receivers, setReceivers] = useState<string[]>([]);
  const [defaultReceiver, setDefaultReceiver] = useState<string>();

  const {
    isOpen: isOpenConfirmModal,
    onOpenModal: handleOpenConfirmModal,
    onCloseModal: handleCloseConfirmModal,
  } = useModal();

  // Inputs
  const HASH_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ];
    return validations;
  }, [t]);

  const CHAIN_VALIDATIONS: Validation[] = useMemo(() => {
    const validations: Validation[] = [
      {
        name: 'required',
        value: true,
        message: t('translation.field_is_required'),
      },
    ];

    return validations;
  }, [t]);
  const ADDRESS_VALIDATIONS: Validation[] = useMemo(() => [], []);
  const FORCE_VALIDATIONS: Validation[] = useMemo(() => [], []);
  const SIDE_VALIDATIONS: Validation[] = useMemo(() => [], []);
  const CID_VALIDATIONS: Validation[] = useMemo(() => [], []);

  const chainField = useInputText('', CHAIN_VALIDATIONS);
  const hashField = useInputText('', HASH_VALIDATIONS);
  const addressField = useInputText('', ADDRESS_VALIDATIONS);
  const forceField = useInputBool(false, FORCE_VALIDATIONS);
  const directionField = useInputText(SideEnum.DEPOSIT, SIDE_VALIDATIONS);
  const cidField = useInputText('', CID_VALIDATIONS);

  // Perform check
  const checkHandler = useCallback(() => {
    if (isEmailConfirmed) {
      if (!loading) {
        if (hashField.isValid && chainField.isValid) {
          const payload: AmlCheckRequest = {
            asset: chainField.value as ChainsEnum,
            hash: hashField.value,
            address: addressField.value,
            side: directionField.value as SideEnum,
            force: forceField.value,
            cid: isComplianceActivated ? cidField.value : '',
          };
          dispatch(amlCheckActions.checkAml(payload));
          forceField.onReset();
        } else {
          if (!hashField.isValid) {
            hashField.onBlur();
          }
          if (!chainField.isValid) {
            chainField.onBlur();
          }
        }
      }
    } else {
      showEmailConfirmationToast();
    }
  }, [hashField, chainField, loading]);

  const [getTxReceivers, fetchingReceivers] = useApiCall(async () => {
    const payload: GetTxReceiversRequest = {
      asset: chainField.value as ChainsEnum,
      hash: hashField.value,
    };
    // Optionally [errorCode] - ErrorCodeEnum.FIELD_REQUIRED
    const list: GetTxReceiversResponse =
      await amlbotAPI.fetchReceivers(payload);

    if (list?.data?.data) {
      setReceivers(list.data.data);
      if (list.data.data.length === 1) {
        setDefaultReceiver(list.data.data[0]);
        addressField.onChange(list.data.data[0]);
      }
    }
  });

  // Effects
  useEffect(() => {
    return () => {
      dispatch(amlCheckActions.setErrorCode(null));
      dispatch(amlCheckActions.setError(null));
    };
  }, []);

  useEffect(() => {
    if (
      amlErrorCode &&
      [ErrorCodeEnum.FORCE_REQUIRED, ErrorCodeEnum.PRECHECK_REQUIRED].includes(
        amlErrorCode,
      )
    ) {
      handleOpenConfirmModal();
    }
  }, [amlErrorCode]);

  useEffect(() => {
    if (!!amlError) {
      amlRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [amlError]);

  useEffect(() => {
    if (forceField.value) {
      checkHandler();
    }
  }, [forceField.value]);

  const isTxnFlow = suggestions.txs.includes(chainField.value as ChainsEnum);

  useEffect(() => {
    if (chainField.value && isTxnFlow) {
      getTxReceivers();
    }
  }, [chainField.value]);

  const resetOptions = () => {
    const allAssets = Object.keys(ChainsDictionary);
    const allowedAssets =
      modeSupportedChains && modeSupportedChains.length
        ? modeSupportedChains
        : Object.keys(ChainsDictionary);

    setOptions((prevOptions) => {
      const list = prevOptions.map((option) => {
        return {
          ...option,
          isLimited: !allowedAssets.includes(option.value),
        };
      });

      if (allAssets.length > allowedAssets.length) {
        list.sort((a, b) => Number(a.isLimited) - Number(b.isLimited));
      }

      return list;
    });
  };

  useEffect(() => resetOptions(), [modeSupportedChains]);

  // Handlers
  const debouncedRequest = useDebounce(async () => {
    if (!hashField.value) {
      resetOptions();
      return;
    }

    try {
      setWhichLoading(true);

      const payload: WhichHashRequest = {
        hash: hashField.value,
      };
      const response = await amlbotAPI.getWhichHash(payload);

      const {
        data: {
          suggestions: { addresses, txs },
        },
      } = response;

      setSuggestions({ addresses, txs });

      const suggestedChains = addresses.concat(
        txs.filter((item) => addresses.indexOf(item) < 0),
      );

      if (
        suggestedChains.length === 0 ||
        (chainField.value &&
          !suggestedChains.includes(chainField.value as ChainsEnum))
      ) {
        chainField.onChange('');
      }

      setOptions((prevOptions) => {
        return prevOptions.map((option) => {
          return {
            ...option,
            isDisabled: !suggestedChains.includes(option.value as ChainsEnum),
          };
        });
      });
    } catch (theError: unknown) {
      console.log(theError);
    } finally {
      setWhichLoading(false);
    }
  }, DEBOUNCE_DELAY);

  const hashFieldChangeHandler = (newValue: string) => {
    if (isEmailConfirmed) {
      hashField.onChange(newValue.trim());
      chainField.onReset();
      addressField.onReset();
      forceField.onReset();
      setReceivers([]);
      debouncedRequest();
    } else {
      showEmailConfirmationToast();
    }
  };

  const closeConfirmModal = () => {
    forceField.onReset();
    dispatch(amlCheckActions.setErrorCode(null));
    dispatch(amlCheckActions.setError(null));
    handleCloseConfirmModal();
  };

  const triggerForceCheck = () => {
    forceField.onChange(true);
    handleCloseConfirmModal();
  };

  const submitDisabled = !isEmailConfirmed
    ? false
    : loading ||
      whichLoading ||
      fetchingReceivers ||
      !hashField.isValid ||
      !chainField.isValid ||
      (suggestions.addresses.length === 0 && suggestions.txs.length === 0) ||
      (isTxnFlow && (!addressField.value || receiverInvalid));

  const checkButtonText = () => {
    if (loading) return t('translation.btn_checking_state');
    if (whichLoading) return t('translation.btn_validating_state');
    if (fetchingReceivers) return t('translation.btn_receivers_state');
    return (
      <>
        {t('translation.btn_check_state')}
        <span>
          <SvgIcon name="aml" />1
        </span>
      </>
    );
  };

  const validateReceiver = useDebounce(async () => {
    if (!!addressField.value) {
      try {
        setWhichLoading(true);
        const payload: WhichHashRequest = {
          hash: addressField.value,
        };
        const response = await amlbotAPI.getWhichHash(payload);
        const {
          data: {
            suggestions: { addresses },
          },
        } = response;

        if (
          !addresses.length ||
          !addresses.includes(chainField.value as ChainsEnum)
        ) {
          setReceiverInvalid(true);
        } else {
          setReceiverInvalid(false);
        }
      } catch (e) {
        //
      } finally {
        setWhichLoading(false);
      }
    } else {
      setReceiverInvalid(false);
    }
  }, DEBOUNCE_DELAY);

  const receiverChanged = (val: string) => {
    addressField.onChange(val);
    validateReceiver();
  };

  const showAmlError =
    !!amlError && (!amlErrorCode || amlErrorCode === ErrorCodeEnum.VALIDATION);

  const sectionTitle = t('translation.check_addr_title');
  const networkSubtitle = t('translation.specify_network');
  const isPolkaDotWarn = (chainField.value as ChainsEnum) === ChainsEnum.DOT;
  const devModeText = `[${t('translation.dev_mode')}]`;

  return (
    <>
      <AMLCheckTabs activeKey={addressCheckPagePath} />
      <SectionBox>
        {{
          title: (
            <div className={styles.sectionTitle} ref={amlRef}>
              <div className={styles.titleRow}>
                {sectionTitle} <BatchUpload />
              </div>
              {devModeUsed && (
                <div className={styles.devModeTitle}>{devModeText}</div>
              )}
            </div>
          ),
          body: (
            <>
              <div className={styles.fields}>
                <div className={styles.textarea}>
                  <Input
                    id="hash"
                    name="hash"
                    type="text"
                    value={hashField.value}
                    onChange={hashFieldChangeHandler}
                    onBlur={hashField.onBlur}
                    placeholder={t('translation.check_addr_input_placeholder')!}
                    isError={hashField.isDirty && !hashField.isValid}
                    readOnly={loading || whichLoading}
                    required
                    clearable
                  />
                  {hashField.isDirty && !hashField.isValid && (
                    <AlertBox type="danger" small>
                      {{
                        description: hashField.errors[0],
                      }}
                    </AlertBox>
                  )}
                </div>
              </div>

              {showAmlError && (
                <AlertBox type="danger" className={styles.message}>
                  {{
                    description: amlError,
                  }}
                </AlertBox>
              )}
              <div className={styles.subtitle}>{networkSubtitle}</div>

              <ChainSelector
                list={options}
                onSelect={chainField.onChange}
                limited={isAccountLiteMode}
                flow={userData?.amlFlow}
                modeChains={modeSupportedChains || []}
              />

              {serviceOptions?.limitFlowAssets.includes(chainField.value) && (
                <AlertBox
                  type="warning"
                  className={styles.message}
                  iconPlacement="start"
                >
                  {{
                    title: t('translation.limited_coins_info_title'),
                    description: parse(
                      t('translation.limited_coins_info_desc'),
                    ),
                    icon: '☝',
                  }}
                </AlertBox>
              )}

              {isPolkaDotWarn && (
                <AlertBox type="info" className={styles.message}>
                  {{
                    description: t('translation.polkadot_extrinsic_warning'),
                  }}
                </AlertBox>
              )}

              {isTxnFlow && (
                <>
                  <AlertBox className={styles.message}>
                    {{
                      description:
                        parse(t('translation.check_side_top_string')) || null,
                    }}
                  </AlertBox>
                  <div className={styles.direction}>
                    <InputSwitch
                      options={directionOptions}
                      name="direction"
                      onChange={directionField.onChange}
                    />
                  </div>

                  <div className={styles.receivers}>
                    <TransactionReceivers
                      loading={fetchingReceivers}
                      receivers={receivers}
                      onChange={receiverChanged}
                      receiverInvalid={receiverInvalid}
                      valueDefault={defaultReceiver}
                    />
                  </div>
                  {isComplianceActivated && (
                    <div className={styles.monitoring}>
                      <AlertBox>
                        {{
                          description:
                            parse(
                              t('translation.add_cid_for_monitoring_msg'),
                            ) || null,
                        }}
                      </AlertBox>
                      <Input
                        id="cid"
                        name="cid"
                        type="text"
                        value={cidField.value}
                        onChange={cidField.onChange}
                        onBlur={cidField.onBlur}
                        placeholder=""
                      />
                    </div>
                  )}
                </>
              )}

              <Button
                corner="rect"
                className={styles.check}
                onClick={checkHandler}
                disabled={submitDisabled}
              >
                {checkButtonText()}
              </Button>
            </>
          ),
        }}
      </SectionBox>

      <ConfirmActionModal
        title={t('translation.alert_warning_title')}
        isOpen={isOpenConfirmModal}
        onClose={closeConfirmModal}
        onConfirm={triggerForceCheck}
        confirmButtonText={t('translation.precheck_agree_msg')}
        cancelButtonText={t('translation.cancel_btn')}
      >
        {addressField.value
          ? t('translation.precheck_tx_msg')
          : t('translation.precheck_address_msg')}
      </ConfirmActionModal>
    </>
  );
};

export default AmlChecker;
